/**
 * @file Questions personnalisées de l'établissement dans l'étape « Détails supplémentaires » du parcours de réservation.
 * @module packages.common.components.table-reservation.components.CustomQuestionsSection
 *
 * Partagé par le widget et le site public. Chaque question s'affiche dans la
 * langue du client (repli sur la langue par défaut de l'établissement), avec
 * la forme de réponse de son type : Oui / Non, case à cocher ou texte libre.
 * Une question obligatoire porte un astérisque ; l'erreur n'apparaît qu'après
 * que le client a tenté d'avancer, pour ne pas reprocher d'emblée.
 */

import React from 'react';
import PropTypes from 'prop-types';

// @mui
import {alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import Iconify from 'packages/common/components/iconify';

import {useLocalizedText} from 'packages/common/utils/multiLanguageHelper';
import {CUSTOM_QUESTION_TYPES, CUSTOM_QUESTION_ICONS, CUSTOM_ANSWER_TEXT_MAX_LENGTH} from 'packages/common/constants/restaurants/customQuestions';
import {isCustomAnswerProvided, YES_NO_CHOICES, toYesNoChoice, fromYesNoChoice} from 'packages/common/utils/restaurant/customAnswers';
import {clientInputStyles} from '../styles/ReservationFlowStyles';

// ----------------------------------------------------------------------

/**
 * Traduit une clé du bloc, avec repli français quand aucune fonction n'est fournie.
 *
 * @param {Function} [t] - Fonction de traduction i18next
 * @param {string} key - Clé sous `widgets.reservation.form.customQuestions`
 * @param {string} fallback - Texte français de repli
 * @return {string} Texte traduit
 */
const translate = (t, key, fallback) => (t ? t(`widgets.reservation.form.customQuestions.${key}`) : fallback);

/**
 * Libellé d'une question, suivi d'un astérisque si elle est obligatoire.
 *
 * @param {Object} props - Props
 * @param {string} props.text - Texte de la question
 * @param {boolean} props.required - Question obligatoire
 * @return {JSX.Element} Libellé
 */
function QuestionText({text, required}) {
  return (
    <>
      {text}
      {required && (
        <Box component="span" aria-hidden="true" sx={{color: 'error.main', ml: 0.5}}>
          *
        </Box>
      )}
    </>
  );
}

QuestionText.propTypes = {
  text: PropTypes.string.isRequired,
  required: PropTypes.bool,
};

/**
 * En-tête d'une question : icône et libellé, même mise en forme que le bloc bon cadeau.
 *
 * @param {Object} props - Props
 * @param {string} props.type - Type de la question
 * @param {string} props.labelId - Identifiant du libellé, pour l'accessibilité
 * @param {string} [props.htmlFor] - Champ décrit par le libellé (réponse texte)
 * @param {string} props.text - Texte de la question
 * @param {boolean} props.required - Question obligatoire
 * @return {JSX.Element} En-tête
 */
function QuestionHeader({type, labelId, htmlFor, text, required}) {
  return (
    <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5}}>
      <Iconify icon={CUSTOM_QUESTION_ICONS[type]} sx={{fontSize: 20, color: 'primary.main', flexShrink: 0, mt: '1px'}} aria-hidden="true" />
      <FormLabel id={labelId} htmlFor={htmlFor} sx={{typography: 'body2', fontWeight: 600, color: 'text.primary'}}>
        <QuestionText text={text} required={required} />
      </FormLabel>
    </Box>
  );
}

QuestionHeader.propTypes = {
  type: PropTypes.string.isRequired,
  labelId: PropTypes.string.isRequired,
  htmlFor: PropTypes.string,
  text: PropTypes.string.isRequired,
  required: PropTypes.bool,
};

/**
 * Champ de réponse adapté au type de la question.
 *
 * @param {Object} props - Props
 * @param {Object} props.question - Question posée (`{id, type, required}`)
 * @param {string} props.text - Texte localisé de la question
 * @param {*} props.value - Réponse courante
 * @param {Function} props.onChange - `(value) => void`
 * @param {boolean} props.hasError - Réponse obligatoire manquante, à signaler
 * @param {Function} [props.handleInputClick] - Stoppe la propagation des clics (fenêtre du widget)
 * @param {Function} [props.t] - Fonction de traduction i18next
 * @return {JSX.Element} Champ de réponse
 */
function QuestionField({question, text, value, onChange, hasError, handleInputClick, t}) {
  const labelId = `custom-question-${question.id}`;

  if (question.type === CUSTOM_QUESTION_TYPES.CHECKBOX) {
    return (
      <FormControlLabel
        control={<Checkbox size="small" checked={value === true} onChange={(event) => onChange(event.target.checked)} inputProps={{'aria-required': question.required, 'aria-invalid': hasError}} />}
        label={<QuestionText text={text} required={question.required} />}
        sx={{
          'alignItems': 'flex-start',
          'm': 0,
          '& .MuiCheckbox-root': {mt: -0.75, ml: -1},
          '& .MuiFormControlLabel-label': {typography: 'body2', fontWeight: 600, color: 'text.primary', pt: '1px'},
        }}
      />
    );
  }

  if (question.type === CUSTOM_QUESTION_TYPES.YES_NO) {
    return (
      <>
        <QuestionHeader type={question.type} labelId={labelId} text={text} required={question.required} />
        <RadioGroup row value={toYesNoChoice(value)} onChange={(event) => onChange(fromYesNoChoice(event.target.value))} aria-labelledby={labelId} aria-required={question.required} sx={{gap: 2}}>
          <FormControlLabel value={YES_NO_CHOICES.NO} control={<Radio size="small" />} label={translate(t, 'no', 'Non')} />
          <FormControlLabel value={YES_NO_CHOICES.YES} control={<Radio size="small" />} label={translate(t, 'yes', 'Oui')} />
        </RadioGroup>
      </>
    );
  }

  return (
    <>
      <QuestionHeader type={question.type} labelId={`${labelId}-label`} htmlFor={labelId} text={text} required={question.required} />
      <OutlinedInput
        id={labelId}
        fullWidth
        multiline
        minRows={2}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onChange(event.target.value)}
        onClick={handleInputClick}
        onFocus={handleInputClick}
        placeholder={translate(t, 'answerPlaceholder', 'Votre réponse')}
        error={hasError}
        inputProps={{'maxLength': CUSTOM_ANSWER_TEXT_MAX_LENGTH, 'aria-required': question.required}}
        sx={clientInputStyles}
      />
    </>
  );
}

QuestionField.propTypes = {
  question: PropTypes.object.isRequired,
  text: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  handleInputClick: PropTypes.func,
  t: PropTypes.func,
};

// ----------------------------------------------------------------------

/**
 * Liste des questions personnalisées posées par l'établissement.
 *
 * @param {Object} props - Props du composant
 * @param {Array<{id: string, type: string, required: boolean, label: Object}>} props.questions - Questions posées
 * @param {Object<string, *>} props.values - Réponses courantes par identifiant de question
 * @param {Function} props.onAnswerChange - `(questionId, value) => void`
 * @param {boolean} props.showErrors - Signaler les réponses obligatoires manquantes
 * @param {Function} [props.handleInputClick] - Stoppe la propagation des clics (fenêtre du widget)
 * @param {Function} [props.t] - Fonction de traduction i18next
 * @return {JSX.Element|null} Bloc des questions, rien si aucune question
 */
export function CustomQuestionsSection({questions, values, onAnswerChange, showErrors, handleInputClick, t}) {
  const localize = useLocalizedText();

  if (!questions?.length) return null;

  return questions.map((question) => {
    const hasError = Boolean(showErrors && question.required && !isCustomAnswerProvided(question.type, values?.[question.id]));
    return (
      <Box
        key={question.id}
        data-question-id={question.id}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 1,
          border: (theme) => `1px solid ${hasError ? theme.palette.error.main : alpha(theme.palette.grey[500], 0.12)}`,
        }}
      >
        <QuestionField
          question={question}
          text={localize(question.label)}
          value={values?.[question.id]}
          onChange={(value) => onAnswerChange(question.id, value)}
          hasError={hasError}
          handleInputClick={handleInputClick}
          t={t}
        />
        {hasError && <FormHelperText error>{translate(t, 'requiredHint', 'Obligatoire pour continuer')}</FormHelperText>}
      </Box>
    );
  });
}

CustomQuestionsSection.propTypes = {
  questions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        required: PropTypes.bool,
        label: PropTypes.object,
      }),
  ),
  values: PropTypes.object,
  onAnswerChange: PropTypes.func.isRequired,
  showErrors: PropTypes.bool,
  handleInputClick: PropTypes.func,
  t: PropTypes.func,
};
